import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import { useAppLogo } from '../hooks/useAppLogo';
import { authAPI } from '../services/api';
import '../styles/AuthPages.css';

const APP_LOGO = `${process.env.PUBLIC_URL || ''}/logo.png`;

const getSystemTheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'day';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'day';
};

function ForgotPasswordPage() {
  const [theme, setTheme] = useState(getSystemTheme);
  const { logoUrl: appLogoUrl } = useAppLogo(theme);
  const logoUrl = APP_LOGO || appLogoUrl;
  const [email, setEmail] = useState('');
  
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setTheme(e.matches ? 'night' : 'day');
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await authAPI.requestPasswordReset(email);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`auth-page theme-${theme}`}>
      <div className="auth-container">
        <div className="auth-logo">
          {logoUrl && (
            <div className="auth-logo-shell">
              <div className="auth-logo-glow"></div>
              <img src={logoUrl} alt="Frame-Book logo" className="auth-logo-image" />
            </div>
          )}
          <h1>Frame-Book</h1>
          <p>Bike Passport & Service History</p>
        </div>

        <div className="auth-card">
          <h2>Reset Password</h2>
          
          {success ? (
            <div className="success-message">
              <div className="success-icon">
                <Icon name="success" color="success" darkMode={theme === 'night'} size={48} />
              </div>
              <h3>Check Your Email</h3>
              <p>
                If an account with <strong>{email}</strong> exists, we've sent you a 
                password reset link. Please check your inbox (and spam folder).
              </p>
              <p style={{ marginTop: '20px', color: '#666', fontSize: '14px' }}>
                The link will expire in 1 hour.
              </p>
              <div className="auth-footer" style={{ marginTop: '30px' }}>
                <Link to="/login" className="btn btn-primary btn-block">
                  Back to Login
                </Link>
              </div>
            </div>
          ) : (
            <>
              <p style={{ color: '#666', marginBottom: '20px', textAlign: 'center' }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
              
              {error && <div className="error">{error}</div>}
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    placeholder="Enter your email address"
                    autoComplete="email"
                  />
                </div>

                <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <div className="auth-footer">
                <p>
                  Remember your password? <Link to="/login">Login here</Link>
                </p>
                <p style={{ marginTop: '10px' }}>
                  Don't have an account? <Link to="/register">Register here</Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;

